import React, { useState, useEffect } from 'react';
import BreadCrumbs from './BreadCrumbs';
import HeaderComponent from './HeaderComponent';
import FeatureNav from './FeatureNav';
import "react-tabulator/css/tabulator.min.css";
import { Tabulator, FormatModule, DownloadModule, PageModule, ExportModule } from "tabulator-tables";
import "tabulator-tables/dist/css/tabulator.min.css";
import Loader from './Loader';
import { prodApiEndpointUrl, stagingApiEndpointUrl } from 'types/apiEndpoints';
import 'bootstrap-select/dist/css/bootstrap-select.min.css';
import 'bootstrap-select';
import EntityHighestPaidTableStandalone from './EntityHighestPaidEmployeeTableStandalone';

interface AllEntity {
  name: string;
  id: number;
}

interface HighestPaidEmployeeByEntity {
  entity_id: number;
  emp_rank: number;
  fiscal_year: number;
  entity_name: string;
  employee_name: string;
  title: string;
  org1: string;
  wages: number;
  benefits: number;
  total: number;
}

Tabulator.registerModule([FormatModule, DownloadModule, PageModule, ExportModule]);

const EmployeeHighestPaidEmployee = () => {
  const [selectedEntityId, setSelectedEntityId] = useState<number>(0);
  const [selectedEntities, setSelectedEntities] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [highestPaidData, setHighestPaidData] = useState<HighestPaidEmployeeByEntity[]>([]);
  const [loadingHighestPaid, setLoadingHighestPaid] = useState<boolean>(false);
  const [entity, setEntity] = useState<string | null>(null);
  const [allEntities, setAllEntities] = useState<AllEntity[]>([]);
  const [fiscalYear, setFiscalYear] = useState<string | null>(null);
  const [apiEndpoint] = useState<string>(() => {
    return window.location.hostname === "transparent.utah.gov" ? prodApiEndpointUrl : stagingApiEndpointUrl;
  });

  const currentYear: number = 2025;
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

  const formatEntityString = (value: string) => {
    if (!value) return '';
    const parts = value.split('_');
    const capitalized = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
    return parts.map(capitalized).join(' ');
  };

  const handleFiscalYearChange = (newYear: string | null) => {
    setHighestPaidData([]);
    setFiscalYear(newYear);
  }

  const highestPaidEmployeesSearchString = `/?function=getAllEntities&parameter=%7B%7D`;

  const buildApiUrl = (apiEndpointUrl: string = apiEndpoint, searchParameter: string = highestPaidEmployeesSearchString) => {
    return `${apiEndpointUrl}${searchParameter}`;
  }

  const getAllEntities = async (): Promise<AllEntity[]> => {
    try {
      const response = await fetch(buildApiUrl());
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const entityArray = data
        .filter((e: { entity_id: number; entity_name: string | number }) => e.entity_id && e.entity_name)
        .map((e: { entity_id: number; entity_name: string | number }) => ({
          name: String(e.entity_name),
          id: e.entity_id,
        }));
      return entityArray;
    } catch (err) {
      return [];
    }
  };

  // Fetch all entities and preload all tab data
  useEffect(() => {
    setLoading(true);
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchEntitiesAndAllData = async () => {
      try {
        const entities = await getAllEntities();
        setAllEntities(entities);

        if (entities.length > 0 && selectedEntityId === 0) {
          setSelectedEntities([entities[0].name]);
        }

        if (!selectedEntityId) return;
        setLoadingHighestPaid(true);

        const highestPaidParam = {
          entity_id: selectedEntityId,
          fiscal_year: fiscalYear ? Number(fiscalYear): 2024
        }

        const highestPaidEncodedParam = encodeURIComponent(JSON.stringify(highestPaidParam));

        const highestPaidEmployeesByEntityIdSearchString = `/?function=getHighestPaidEmployeesByEntityID&parameter=${highestPaidEncodedParam}`;

        // Fetch Data - Updated Version to use apiEndpoint
        const fetchHighestPaidByEntity = fetch(
          buildApiUrl(apiEndpoint, highestPaidEmployeesByEntityIdSearchString),
          { signal }
        ).then(res => (res.ok ? res.json() : []));

        const [highestPaidByEntity] = await Promise.all([
          fetchHighestPaidByEntity
        ]);

        if (!signal.aborted) {
          setHighestPaidData(Array.isArray(highestPaidByEntity) ? highestPaidByEntity : [highestPaidByEntity]);
        }

      } catch (err: any) {
      } finally {
        setLoading(false);
        setLoadingHighestPaid(false);
      }
    };

    fetchEntitiesAndAllData();
    return () => controller.abort();
  }, [selectedEntityId, fiscalYear]);
 
  return (
    <div>
      <HeaderComponent/>
      <FeatureNav/>
      <BreadCrumbs/>
      {loading ? <Loader /> : <></>}
      <EntityHighestPaidTableStandalone
        key={selectedEntityId} // Added key to force remount on entity change
        selectedEntityId={selectedEntityId}
        formatCurrency={formatCurrency}
        formatEntityString={formatEntityString}
        data={highestPaidData}
        loading={loadingHighestPaid} // Changed to highest paid loading state to prevent stale data
        entityName={entity ?? undefined}
        fiscalYear={fiscalYear ?? currentYear.toString()}
        onFiscalYearChange={handleFiscalYearChange}
        onEntityChange={setSelectedEntityId}
      />
    </div>
  );
};

export default EmployeeHighestPaidEmployee;